import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface SyncConfigRow {
  id: string;
  appId: string;
  appKey: string;
  appName: string;
  direction: string;
  frequency: string;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;
}

const DEFAULT_FREQS = ['hourly', 'nightly', 'weekly', 'manual'];

export default function SyncManagerPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'syncs'],
    queryFn: () => api.get<SyncConfigRow[]>('/api/syncs'),
  });

  const patch = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { frequency?: string; isEnabled?: boolean };
    }) => api.patch(`/api/syncs/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'syncs'] }),
  });

  const runSync = useMutation<
    { id: string },
    Error,
    { appId: string; direction: string }
  >({
    mutationFn: ({ appId, direction }) =>
      api.post<{ id: string }>(`/api/syncs/${appId}/${direction}/run`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'syncs'] });
      qc.invalidateQueries({ queryKey: ['admin', 'logs'] });
    },
  });

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-slate-200 rounded-xl" />;
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
        Erreur de chargement.{' '}
        <button type="button" className="underline" onClick={() => refetch()}>
          Réessayer
        </button>
      </div>
    );
  }

  const rows = data ?? [];
  const byApp = new Map<string, { appName: string; appKey: string; inbound?: SyncConfigRow; outbound?: SyncConfigRow }>();

  for (const r of rows) {
    if (!byApp.has(r.appId)) {
      byApp.set(r.appId, { appName: r.appName, appKey: r.appKey });
    }
    const g = byApp.get(r.appId)!;
    if (r.direction === 'inbound') g.inbound = r;
    if (r.direction === 'outbound') g.outbound = r;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">Gestion des synchronisations</h2>
        <p className="text-sm text-slate-600 mt-1">Fréquences, activation et lancement manuel</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100">
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Application</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Inbound</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Outbound</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 w-40">Sync Now</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[...byApp.entries()].map(([appId, g]) => (
              <tr key={appId} className="hover:bg-slate-50/80">
                <td className="px-4 py-3 font-medium text-slate-800">{g.appName}</td>
                <td className="px-4 py-3">
                  {g.inbound ? (
                    <ConfigCell
                      row={g.inbound}
                      onPatch={(body) => patch.mutate({ id: g.inbound!.id, body })}
                      disabled={patch.isPending}
                    />
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3">
                  {g.outbound ? (
                    <ConfigCell
                      row={g.outbound}
                      onPatch={(body) => patch.mutate({ id: g.outbound!.id, body })}
                      disabled={patch.isPending}
                    />
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      disabled={runSync.isPending || !g.inbound}
                      onClick={() => g.inbound && runSync.mutate({ appId, direction: 'inbound' })}
                      className="text-xs px-2 py-1 rounded bg-orange-600 text-white disabled:opacity-40 hover:bg-orange-700"
                    >
                      Inbound
                    </button>
                    <button
                      type="button"
                      disabled={runSync.isPending || !g.outbound}
                      onClick={() => g.outbound && runSync.mutate({ appId, direction: 'outbound' })}
                      className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
                    >
                      Outbound
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {runSync.isSuccess && (
        <p className="text-sm text-emerald-700">
          Run créé : log id {runSync.data?.id}
        </p>
      )}
      {runSync.isError && (
        <p className="text-sm text-red-600">{(runSync.error as Error).message}</p>
      )}
    </div>
  );
}

function ConfigCell({
  row,
  onPatch,
  disabled,
}: {
  row: SyncConfigRow;
  onPatch: (body: { frequency?: string; isEnabled?: boolean }) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <select
        value={row.frequency}
        disabled={disabled}
        onChange={(e) => onPatch({ frequency: e.target.value })}
        className="block w-full max-w-[140px] text-xs border border-slate-200 rounded-lg px-2 py-1"
      >
        {[...new Set([...DEFAULT_FREQS, row.frequency])].map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={row.isEnabled}
          disabled={disabled}
          onChange={(e) => onPatch({ isEnabled: e.target.checked })}
        />
        Actif
      </label>
      <div className="text-xs text-slate-400">
        {row.lastRunAt
          ? new Date(row.lastRunAt).toLocaleString('fr-CA')
          : '—'}{' '}
        · {row.lastRunStatus ?? '—'}
      </div>
    </div>
  );
}
